// Sponsored posting: the author signs, Twetch pays.
//
// The wire body carries STRUCTURED fields plus the wallet's AIP signature —
// never a txHex, never paidSats (the server rejects any payout-bearing
// field; the paymaster funds the miner fee out of band). One wallet popup
// per post is the locked UX: `signWithWallet` is invoked EXACTLY once per
// submit attempt, and a retry after an expired signature is a new explicit
// attempt (the timestamp lives inside the signed domain, so a fresh ts
// requires a fresh signature — there is no silent re-sign).

import { TWETCH_API, twetchBridgeJson, TwetchError, type TwetchSmartTag } from './twetch-api'
import { postAipText } from './twetch-aip'
import { TWETCH_ALGORITHM, loginWithWallet, type TwetchIdentity } from './twetch-auth'
import { loadDeviceKey, newDeviceKey, storeDeviceKey, type DeviceKey } from './twetch-crypto'
import { migrateOwProfile } from './ow-profile'
import { thesisAipText, thesisMatches, type ThesisAnnouncement } from './twetch-thesis'
import { twetchSignedPost, twetchSignedPostDirect } from './twetch-transport'
import { walletSignatureString } from './wallet-signature'

/// The exact wire body for a sponsored `POST /v1/posts`.
interface SponsoredPostBody {
	userId: number
	content: string
	smartTags?: TwetchSmartTag[]
	metadataVersion: 2
	agentPubkey?: string
	replyPostId?: number
	sponsored: true
	aip: {
		algorithm: typeof TWETCH_ALGORITHM
		address: string
		signature: string
		ts: number
	}
}

export type PostFailure =
	| 'ineligible'
	| 'unavailable'
	| 'expired'
	| 'replay'
	| 'too-large'
	| 'identity-mismatch'
	| 'verification'
	| 'rejected'
	| 'other'

/// Classify a sponsored-post rejection onto its UX branch. 403 = not
/// eligible (allowlist / no external identity), 503 = paymaster down or
/// daily budget exhausted; the 400 family splits on the server's message.
/// Twetch refusing a session to a wallet that is linked to, rather than the
/// root of, its account. Not a failure of the post — only of the session.
export function isLinkedWalletRefusal(e: unknown): boolean {
	const status = e instanceof TwetchError ? e.status : undefined
	return status === 403 && /linked wallets? can/i.test(String((e as { message?: string })?.message ?? ''))
}

export function classifyPostError(e: unknown): PostFailure {
	const status = e instanceof TwetchError ? e.status : undefined
	const msg = String((e as { message?: string })?.message ?? '')
	if (status === 403 || /not eligible/i.test(msg)) {
		if (/verification|turnstile|human|account creation/i.test(msg)) {
			return 'verification'
		}
		return 'ineligible'
	}
	if (status === 503 || /unavailable|budget|paymaster/i.test(msg)) {
		return 'unavailable'
	}
	if (/expired|too old|stale|window/i.test(msg)) {
		return 'expired'
	}
	if (/replay|already used|duplicate signature/i.test(msg)) {
		return 'replay'
	}
	if (/too (long|large)|length|size/i.test(msg)) {
		return 'too-large'
	}
	if (/mismatch|identity|address|signature/i.test(msg)) {
		return 'identity-mismatch'
	}
	if (typeof (e as { message?: unknown })?.message === 'string' && msg && status !== undefined) {
		return 'rejected'
	}
	return 'other'
}

/// The user-facing sentence for each branch — every claim in these is real:
/// the composer renders them verbatim.
export function postFailureMessage(kind: PostFailure, serverMessage: string): string {
	switch (kind) {
		case 'ineligible':
			// Several different refusals arrive as 403 — an exhausted one-time
			// grant, a wallet this account does not own, a paused class. Only
			// the server knows which, so say what it said and keep the generic
			// sentence for the generic refusal.
			return /not eligible/i.test(serverMessage) || !serverMessage.trim()
				? "Sponsored posting isn't enabled for this account yet."
				: serverMessage
		case 'unavailable':
			return 'Sponsored posting is temporarily unavailable — try again in a few minutes.'
		case 'expired':
			return 'Your signature expired before it reached Twetch — sign again to publish.'
		case 'replay':
			return 'Twetch already saw this exact signature — sign again to publish.'
		case 'too-large':
			return 'This post is too large for Twetch. Shorten it and try again.'
		case 'identity-mismatch':
			return "The signature didn't match your registered wallet. Reconnect the wallet you joined with and try again."
		case 'verification':
			return 'First-post verification is not ready. Wait a moment and try again.'
		default:
			return serverMessage || "Couldn't publish the post."
	}
}

export interface SponsoredPostResult {
	/// Whatever the server returned for the created post — id/txid when present.
	post: { id?: number; txid?: string | null } | null
	/// Set when this post silently created or restored the wallet-rooted session.
	identity: TwetchIdentity
}

interface FirstPostSessionResponse {
	userId: number
	existing: boolean
	metadataVersion: 2
	agentPubkey: string
	tagRefs: string[]
	smartTags: TwetchSmartTag[]
	externalIdentity: { algorithm: typeof TWETCH_ALGORITHM; address: string }
}

/// Canonical namespace identities signed into the readable MAP `tags` field.
/// Symbols stay in the visible post and are resolved from each registry.
export function canonicalSmartTagRefs(smartTags: TwetchSmartTag[]): string[] {
	return Array.from(
		new Set(smartTags.map((tag) => `${tag.namespace.trim().toLowerCase()}:${tag.id.trim()}`))
	).slice(0, 8)
}

/// Whether Twetch already knows this wallet — the public address index, no
/// signature. A failed lookup reads as "no": the first-post path still works.
async function accountExists(address: string): Promise<boolean> {
	try {
		const res = await fetch(`${TWETCH_API}/v1/users/by-addresses`, {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ addresses: [address] })
		})
		if (!res.ok) return false
		const body = (await res.json()) as { users?: Record<string, unknown> }
		return !!body.users?.[address]
	} catch {
		return false
	}
}

/// Submit one sponsored post.
///
/// `signWithWallet` is the wallet's message-sign door. It is called once for
/// an ordinary post and once for a thesis from an established session; a
/// browser with no session spends a second signature opening one (signing in,
/// or the first-post route for a wallet that cannot sign in), which is what
/// buys a first post the right to be a thesis at all.
///
/// Throws TwetchError (or the wallet's own error) — callers branch via
/// classifyPostError.
export async function sendSponsoredPost(opts: {
	identity: TwetchIdentity | null
	deviceKey: DeviceKey | null
	address: string
	content: string
	smartTags?: TwetchSmartTag[]
	turnstileToken?: string | null
	/// Reply linkage: the parent post's id AND its on-chain txid (the AIP
	/// domain binds the txid; the API row binds the id).
	reply?: { postId: number; txid: string | null } | null
	/// Set when this post is a THESIS on one asset: it publishes as a Twetch
	/// system post (activity) that still carries this smart tag.
	thesisTag?: TwetchSmartTag | null
	signWithWallet: (text: string) => Promise<string>
	/// The wallet's signer for a sign-in challenge, when one is needed before
	/// the post (see the thesis note below). Defaults to `signWithWallet`.
	signLogin?: (text: string) => Promise<string>
	onProvisioned?: (identity: TwetchIdentity) => void
	now?: () => number
}): Promise<SponsoredPostResult> {
	const ts = (opts.now ?? Date.now)()
	let identity = opts.identity
	let deviceKey = opts.deviceKey
	// A thesis needs an established session (minting its announcement is a
	// device-key signed call). A browser that has none — a new device, another
	// origin, after "Disconnect Twetch" — but whose wallet ALREADY has an
	// account signs in first, so the thesis still publishes as a thesis instead
	// of silently degrading to an ordinary post. Two popups, once per browser.
	// A wallet with no account keeps the first-post path below: that post is
	// what creates the account.
	if ((!identity || !deviceKey) && opts.thesisTag && !opts.reply && (await accountExists(opts.address))) {
		try {
			identity = await loginWithWallet({
				address: opts.address,
				signMessage: opts.signLogin ?? opts.signWithWallet
			})
			deviceKey = loadDeviceKey()
			opts.onProvisioned?.(identity)
		} catch (e) {
			// A wallet LINKED to a seed-rooted account cannot sign IN — opening
			// a session rotates that account's key, so Twetch refuses. It can
			// still open one through the first-post route below, which resolves
			// the account the wallet already belongs to. Falling through costs
			// the same two popups as signing in, and still publishes a thesis.
			if (!isLinkedWalletRefusal(e)) {
				throw e
			}
			identity = null
			deviceKey = null
		}
	}
	const bootstrapping = !identity || !deviceKey

	if (bootstrapping) {
		deviceKey = newDeviceKey()
	}
	let smartTags = opts.smartTags ?? []
	const agentPubkey = bootstrapping ? deviceKey!.pub.toLowerCase() : null
	const tagRefs = canonicalSmartTagRefs(smartTags)
	// Signed only when something actually needs it. A thesis from an
	// established session signs its own announcement below and never this —
	// asking for both would cost a popup nobody spends today.
	let postSignature: string | null = null
	const signPost = async (): Promise<string> => {
		if (postSignature !== null) {
			return postSignature
		}
		const text = await postAipText(opts.content, opts.reply?.txid ?? null, ts, {
			tagRefs,
			agentPubkey
		})
		const signed = walletSignatureString(await opts.signWithWallet(text))
		postSignature = signed
		return signed
	}
	if (bootstrapping) {
		const signature = await signPost()
		const session = await twetchBridgeJson<FirstPostSessionResponse>(
			'/v1/auth/first-post-session',
			{
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({
					devicePubkey: deviceKey!.pub,
					metadataVersion: 2,
					...(opts.turnstileToken ? { turnstileToken: opts.turnstileToken } : {}),
					content: opts.content,
					...(smartTags.length ? { smartTags } : {}),
					aip: {
						algorithm: TWETCH_ALGORITHM,
						address: opts.address,
						signature,
						ts
					}
				})
			}
		)
		if (
			session.metadataVersion !== 2 ||
			session.agentPubkey.toLowerCase() !== agentPubkey ||
			JSON.stringify(session.tagRefs) !== JSON.stringify(tagRefs)
		) {
			throw new Error('Twetch returned a different first-post signature binding.')
		}
		smartTags = session.smartTags
		storeDeviceKey(deviceKey!)
		identity = {
			uid: session.userId,
			handle: String(session.userId),
			address: session.externalIdentity.address,
			algorithm: session.externalIdentity.algorithm,
			devicePubkey: deviceKey!.pub
		}
		opts.onProvisioned?.(identity)
		// The account exists now, and it is blank: the first-post route mints it
		// with no name and no picture. A wallet that already has both on
		// Ordinals Wallet arrives wearing them, not as a bare number.
		await migrateOwProfile({ uid: identity.uid, deviceKey: deviceKey! })
	}
	if (!identity || !deviceKey) {
		throw new Error('Twetch could not establish a posting session.')
	}
	// A thesis goes out as a system post, and this runs AFTER the session
	// exists — so a wallet's very first post is a thesis too. Minting the
	// announcement needs an account, which by here every path has: an
	// established session, a sign-in, or the first-post route just above
	// (the only door a LINKED wallet can walk through). The signature that
	// route spent bought the session; the thesis signs itself below. A thesis
	// still falls back to the ordinary post when Twetch doesn't offer the
	// endpoint (404/405).
	if (opts.thesisTag && !opts.reply) {
		const auth = { uid: identity!.uid, deviceKey: deviceKey! }
		let announcement: ThesisAnnouncement | null = null
		try {
			const minted = await twetchSignedPostDirect<{ announcement: ThesisAnnouncement }>(
				'/v1/system-posts/thesis',
				{ content: opts.content, smartTag: opts.thesisTag },
				auth
			)
			announcement = minted.announcement
		} catch (e) {
			if (!(e instanceof TwetchError) || (e.status !== 404 && e.status !== 405)) {
				throw e
			}
		}
		if (announcement) {
			if (!thesisMatches(announcement, identity!.uid, opts.content, opts.thesisTag)) {
				throw new Error('Twetch prepared a different thesis than the one you wrote.')
			}
			const signature = walletSignatureString(await opts.signWithWallet(thesisAipText(announcement, ts)))
			const created = await twetchSignedPost<{ id?: number; txid?: string | null }>(
				'/v1/posts',
				{
					userId: identity!.uid,
					content: announcement.content,
					metadataVersion: 2,
					systemPostId: announcement.id,
					sponsored: true,
					aip: {
						algorithm: TWETCH_ALGORITHM,
						address: identity!.address,
						signature,
						ts
					}
				},
				auth
			)
			return { post: created ?? null, identity: identity! }
		}
	}

	const body: SponsoredPostBody = {
		userId: identity.uid,
		content: opts.content,
		...(smartTags.length ? { smartTags } : {}),
		metadataVersion: 2,
		...(agentPubkey ? { agentPubkey } : {}),
		...(opts.reply ? { replyPostId: opts.reply.postId } : {}),
		sponsored: true,
		aip: {
			algorithm: TWETCH_ALGORITHM,
			address: identity.address,
			signature: await signPost(),
			ts
		}
	}
	const created = await twetchSignedPost<{ id?: number; txid?: string | null }>('/v1/posts', body, {
		uid: identity.uid,
		deviceKey
	})
	return { post: created ?? null, identity }
}
